import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useApi } from '../api/ApiContext'
import type { ItemType, SyncItem } from '../api/types'
import { MESSAGES_QUERY_KEY } from '../features/messages/useMessages'
import { applyPull, EMPTY_CACHE, groupByType, mergeItems, pullSince, type ItemCache } from './cache'

/** One cache per type. The weight page keeps its own `['sync', 'WEIGHT']` query of parsed weigh-ins. */
export const itemsKey = (type: ItemType) => ['items', type] as const

/**
 * The items of [type] as this tab holds them, parsed by [select] (a stable, module-level function, so
 * React Query re-runs it only when the items change). Each refetch pulls only what changed since the
 * type's high-water mark, minus the contract's overlap.
 */
export function useSynced<T>(type: ItemType, select: (items: readonly SyncItem[]) => T) {
  const api = useApi()
  const queryClient = useQueryClient()
  const selectCache = useCallback((cache: ItemCache) => select(cache.items), [select])
  return useQuery({
    queryKey: itemsKey(type),
    queryFn: async () => {
      const previous = queryClient.getQueryData<ItemCache>(itemsKey(type)) ?? EMPTY_CACHE
      return applyPull(previous, await api.pullAll({ since: pullSince(previous), type }))
    },
    select: selectCache,
  })
}

/** Every item of the type, tombstones included. */
export const allItems = (items: readonly SyncItem[]) => items

/**
 * Pushes items. Accepted items go into the caches straight away; any rejection (the server holds a newer
 * copy) fails the mutation after that, and the refetch brings the newer copy in.
 */
export function usePush() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (items: SyncItem[]) => {
      const result = await api.push(items)
      const accepted = new Set(result.accepted)
      const stored = items.filter((item) => accepted.has(`${item.type}#${item.id}`))
      for (const [type, group] of groupByType(stored)) {
        queryClient.setQueryData<ItemCache>(itemsKey(type), (cache) => mergeItems(cache ?? EMPTY_CACHE, group))
      }
      if (result.rejected.length > 0) {
        throw new Error(
          `${result.rejected.length} ${result.rejected.length === 1 ? 'change was' : 'changes were'} not saved: another device saved a newer version`,
        )
      }
      return stored
    },
    onSettled: async (_result, _error, items) => {
      const types = new Set(items.map((item) => item.type))
      await Promise.all([
        ...[...types].flatMap((type) => [
          queryClient.invalidateQueries({ queryKey: itemsKey(type) }),
          queryClient.invalidateQueries({ queryKey: ['sync', type] }),
        ]),
        // In-app messages are fetched "on load and after sync" (docs/plan/notifications.md "In-app"); a push is
        // the web's only sync step with a clear end to hook, so this is where "after sync" happens here.
        queryClient.invalidateQueries({ queryKey: MESSAGES_QUERY_KEY }),
      ])
    },
  })
}
